import { CharacterEditForm } from '@hooks/useChapter';
import { ChatItemTypes } from 'chatie-viewer-v2';
import { EFinderImageObject } from 'chatie-viewer-v2/dist/utils/efinder';
import { Chat, Effect } from 'chatie-viewer-v2/dist/utils/types';
import { v4 as uuidv4 } from 'uuid';

import { CHAT_TYPE, INITIAL_GENERAL_BUBBLE_STYLE } from '@constants';
import {
  ICharacterCreateForm,
  IChat,
  IChatProfile,
  IChemingCharacterCreateForm,
  IEFinderObject,
} from '@model';
import { formatComma, getJosa } from '@utils/misc';

export enum ChatTypes {
  Sender = 'sender',
  Receiver = 'receiver',
  Paragraph = 'paragraph',
  Narration = 'narration',
  Custom = 'custom',
  ReceiverImage = 'receiver_image',
}

export enum MessageTypes {
  Sender = 'sender',
  Receiver = 'receiver',
  Start = 'start',
  Temp = 'temp',
}
export interface ChatbotChat {
  id: string;
  key: string;
  content: string;
  type: string;
  isProfileShow?: boolean;
  // style?: ChatStyle;
}
export interface EditorChat {
  id: string;
  characterId?: string;
  object: { text: string };
  objectType: string;
  isShowProfile?: boolean;
  position?: 'left' | 'right';
  // style?: ChatStyle;
}

export interface EditorCharacter {
  id: string;
  displayName: string;
  isPov?: boolean;
  profileImage?: IEFinderObject<'image'> | undefined | null;
  position?: 'left' | 'right';
}

export type ChatType =
  | 'sender'
  | 'receiver'
  | 'paragraph'
  | 'narration'
  | 'custom';

function splitEditorSentences(text: string) {
  const regex = /[.?!]\s|\n\n|\n|\s{2,}/g;

  const matches = text.matchAll(regex);
  const indices =
    Array.from(matches, (match) => (match?.index && match.index + 1) || 0) ||
    [];

  const parts = [];
  if (indices.length > 0) {
    for (let i = 0; i < indices.length; i++) {
      const startIndex = i === 0 ? 0 : indices[i - 1] + 1;
      const endIndex = indices[i];
      const part = text.substring(startIndex, endIndex);
      parts.push({ content: part.replace(/\\/g, '') });
    }

    if (indices.length > 0) {
      const lastPart = text.substring(indices[indices.length - 1] + 1);
      parts.push({ content: lastPart.replace(/\\/g, '') });
    }
  } else {
    parts.push({ content: text.replace(/\\/g, '') });
  }

  return parts;
}

const getInitialProfile = (name: string) => {
  const id = uuidv4();
  return {
    displayName: name,
    id: id,
    isPov: false,
    name: name,
    profileImage: null,
  };
};

export const parseEditorChats = (
  string: string,
  lastId: string,
  characters: EditorCharacter[],
  isV2Story = false
) => {
  const result: EditorChat[] = [];
  const newCharacters: EditorCharacter[] = [...characters];
  const speakerRegex = /\n\n|\n/;
  const hasName = string?.includes(':');

  const parts = string.split(speakerRegex);

  parts.map((part) => {
    let curLastId = lastId;
    const content = part.replace(/[\n\n\\]|/g, '').trim();
    const paragraphName = '지문:';
    const name = content.split(':')[0];
    const isParagraph = name === '지문' || !hasName;
    const isNarration = name === '나레이션';

    if (!isParagraph) {
      const name = content.split(':')[0];
      const parts = content
        .split(/(".*?"|[^"]+)(?=\s*")?/g)
        .filter((part) => part.trim() !== '');
      parts.map((splitedPart) => {
        const r = splitedPart
          ?.replaceAll('"', '')
          ?.replaceAll(`${name}:`, '')
          .trim();

        if (r === '') {
          return;
        }

        const usedCharacter = newCharacters?.find((c) => c.displayName === name)
          ? true
          : false;
        let character: EditorCharacter | undefined;
        if (usedCharacter) {
          character = newCharacters?.find((c) => c.displayName === name);
        } else {
          character = getInitialProfile(name);
          if (name !== '나레이션') {
            newCharacters.push(character as EditorCharacter);
          }
        }

        const splitedR = splitEditorSentences(r);
        splitedR.map((res) => {
          let isProfileShow = false;
          if (character?.id != curLastId && character?.id !== '0') {
            isProfileShow = true;
          }

          curLastId = character?.id as string;
          const key = uuidv4();
          const position = isV2Story
            ? isNarration
              ? undefined
              : character?.position
              ? character?.position
              : 'left'
            : undefined;
          if (character?.position && isV2Story) {
            result.push({
              object: { text: res.content },
              objectType: isNarration ? 'narration' : 'text',
              id: key,
              isShowProfile: isProfileShow,
              characterId: isNarration ? undefined : (character?.id as string),
              position: position,
            });
          } else {
            result.push({
              object: { text: res.content },
              objectType: isNarration ? 'narration' : 'text',
              id: key,
              isShowProfile: isProfileShow,
              characterId: isNarration ? undefined : (character?.id as string),
              position: position,
            });
          }
        });
      });
    } else {
      const parts = content
        .split(/(".*?"|[^"]+)(?=\s*")?/g)
        .filter((part) => part.trim() !== '');
      parts.map((splitedPart) => {
        const trimmedResult = splitedPart
          ?.replaceAll('"', '')
          ?.replaceAll(`${name}:`, '')
          ?.replaceAll(paragraphName, '')
          ?.replaceAll('"', '')
          ?.replaceAll('*', '')
          .trim();

        if (trimmedResult === '') {
          return;
        }
        const splitedResult = splitEditorSentences(trimmedResult);
        splitedResult.map((res) => {
          const key = uuidv4();
          const isProfileShow = false;
          curLastId = '';
          result.push({
            object: {
              text: res.content,
            },
            objectType: 'text',
            id: key,
            isShowProfile: isProfileShow,
            characterId: '0',
          });
        });
      });
    }
  });

  return [result, newCharacters];
};

export function parseSendMessage(content: string) {
  const regex = /(\*.*?\*)/;
  const splitContent = content.split(regex);
  const arr = [];
  for (let i = 0; i < splitContent?.length; i++) {
    const item = splitContent[i].replace(/\\/g, '');

    const removed = item.replaceAll('\n', '');
    if (!removed?.startsWith('*') && removed) {
      arr.push('"' + removed + '"');
    } else if (removed) {
      arr.push(removed);
    }
  }
  const joinedContent = arr.join('\n');
  return joinedContent;
}

function splitSentences(text: string, isParagraph: boolean) {
  const regex = /[.?!]\s/g;

  const matches = text.matchAll(regex);
  const indices =
    Array.from(matches, (match) => (match?.index && match.index + 1) || 0) ||
    [];

  const parts = [];
  if (indices.length > 0) {
    for (let i = 0; i < indices.length; i++) {
      const startIndex = i === 0 ? 0 : indices[i - 1] + 1;
      const endIndex = indices[i];
      const part = text.substring(startIndex, endIndex);
      parts.push({ content: part.replace(/\\/g, ''), isParagraph });
    }

    if (indices.length > 0) {
      const lastPart = text.substring(indices[indices.length - 1] + 1);
      parts.push({ content: lastPart.replace(/\\/g, ''), isParagraph });
    }
  } else {
    parts.push({ content: text.replace(/\\/g, ''), isParagraph });
  }
  return parts;
}

export const parseChatString = (string: string, isAiChat: boolean) => {
  const result: { content: string; isParagraph: boolean; type: string }[] = [];

  const regex = /(\".*?\")|(\*.*?\*)|(?=\n\n)/;
  const arr = string.split(regex);
  arr
    .filter((line) => line && Boolean(line.trim()))
    .map((item) => {
      const isParagraph = item.startsWith('*');
      const type = isParagraph
        ? 'general'
        : item.startsWith('"')
        ? 'text'
        : 'general';
      const content = item.replace(/[*"\n\n\\]|/g, '').trim();

      if (!isParagraph && isAiChat) {
        const res = splitSentences(content, isParagraph);
        result.push(...res.map((r) => ({ ...r, type })));
      } else {
        result.push({ content, isParagraph, type });
      }
    });

  return result;
};

export const parseUserChatString = (string: string) => {
  const result: { content: string; type: string }[] = [];

  const regex = /\*(.*?)\*/gs;
  const matches = string.matchAll(regex);

  let lastIndex = 0;
  for (const match of matches) {
    const start = match.index!;
    const end = start + match[0].length;

    if (start > lastIndex) {
      const text = string.slice(lastIndex, start).trim();
      if (text) {
        result.push({ content: text, type: 'text' });
      }
    }

    const general = match[1].trim();
    if (general) {
      result.push({ content: general, type: 'general' });
    }

    lastIndex = end;
  }

  if (lastIndex < string.length) {
    const text = string.slice(lastIndex).trim();
    if (text) {
      result.push({ content: text, type: 'text' });
    }
  }

  return result.filter((item) => item.content !== '');
};

export const getGeneralChat = (id: string, chat: string) => {
  const GRAY_700 = '#5B5B5B';
  return {
    character: {
      displayName: 'General',
      id: '0',
      isPov: false,
      name: 'General',
      isOmniscient: true,
      isSender: false,
    },
    character_id: '0',
    id: id,
    object: { text: chat },
    objectType: ChatItemTypes.Text,
    isProfileShow: false,
    style: {
      bubbleColor: '"rgba(255, 255, 255, 0)"',
      textColor: GRAY_700,
      textAlign: 'center',
      textStyle: '"initial"',
      fontWeight: '"bold"',
      wordBreak: 'normal',
    },
  };
};
export const parseStartMessages = (
  item: string,
  lastType: string,
  type: ChatType
): ChatbotChat => {
  const isParagraph = type === ChatTypes.Paragraph;
  const content = item
    .replace(/[*"\n\n\\]|/g, '')
    .trim()
    ?.replace(/[""\n\n\\]|/g, '')
    ?.trim();
  if (!isParagraph) {
    let isProfileShow = false;
    if (
      type != lastType &&
      (type === ChatTypes.Sender || type === ChatTypes.Receiver)
    ) {
      isProfileShow = true;
    }
    const key = uuidv4();
    return {
      content: content,
      type,
      key,
      id: key,
      isProfileShow,
    };
  } else {
    const key = uuidv4();
    const isProfileShow = false;

    return {
      content,
      type,
      key: key,
      id: key,
      isProfileShow: isProfileShow,
    };
  }
};
export const parseStartText = (string: string, id: string, name: string) => {
  const regex = /(\*.*?\*)|(?=\n\n)/;
  const arr = string.replace(/"/g, '').split(regex);

  const startNarration = {
    id: uuidv4(),
    object: {
      text: `${getJosa(name, '과')} 이야기를 만들어가세요.`,
    },
    objectType: 'narration',
  };
  const result = arr
    .filter((line) => line && Boolean(line.trim()))
    .map((item, index) => {
      const isParagraph = item.startsWith('*');
      const content = item.trim().replace(/[*"\\n]|/g, '');
      return {
        id: index,
        object: { text: content },
        objectType: 'text',
        characterId: isParagraph ? '0' : id,
      };
    });

  return [startNarration, ...result];
};

export type ChatItem = {
  content: string;
  role: 'human' | 'ai';
  chapter_id?: number | null;
};

export const splitChat = (string: string, id: string) => {
  const regex = /(\*.*?\*)|(?=\n\n)/;
  const arr = string.replace(/"/g, '').split(regex);

  const result = arr
    .filter((line) => line && Boolean(line.trim()))
    .map((item) => {
      const tagId = uuidv4();
      const isParagraph = item.startsWith('*');
      const content = item.trim().replace(/[*"\\n]|/g, '');

      return {
        id: tagId,
        object: { text: content },
        objectType: 'text',
        characterId: isParagraph ? '0' : id,
        style: isParagraph ? INITIAL_GENERAL_BUBBLE_STYLE : null,
      };
    });

  return result;
};

function splitPeekSentences(text: string) {
  const regex = /[.?!]\s|\n\n|\n|\s{2,}/g;

  const matches = text.matchAll(regex);
  const indices =
    Array.from(matches, (match) => (match?.index && match.index + 1) || 0) ||
    [];

  const parts = [];
  if (indices.length > 0) {
    for (let i = 0; i < indices.length; i++) {
      const startIndex = i === 0 ? 0 : indices[i - 1] + 1;
      const endIndex = indices[i];
      const part = text.substring(startIndex, endIndex);
      parts.push({ content: part.replace(/\\/g, '') });
    }

    if (indices.length > 0) {
      const lastPart = text.substring(indices[indices.length - 1] + 1);
      parts.push({ content: lastPart.replace(/\\/g, '') });
    }
  } else {
    parts.push({ content: text.replace(/\\/g, '') });
  }
  return parts;
}

export const parseChats = (
  string: string,
  lastType: string,
  defaultType: ChatType = 'receiver'
) => {
  const result: ChatbotChat[] = [];

  let curLastType = lastType;
  const firstRegex = /(\"[^"]*\"|\"|[^"]+)/g;
  const firstArr = string
    ?.split(firstRegex)
    ?.filter((a) => a && a?.trim() !== '');

  firstArr.map((item) => {
    const isParagraph = !item.startsWith('"');

    const content = item
      .replace(/[*"\n\n\\]|/g, '')
      .trim()
      ?.replace(/[""\n\n\\]|/g, '')
      ?.trim();

    if (!isParagraph) {
      const res = splitPeekSentences(content);
      result.push(
        ...res.map((r) => {
          const type = isParagraph ? ('paragraph' as ChatType) : defaultType;
          let isProfileShow = false;

          if (
            type != curLastType &&
            (type === ChatTypes.Sender || type === ChatTypes.Receiver)
          ) {
            isProfileShow = true;
          }
          curLastType = type;
          const key = uuidv4();
          return {
            ...r,
            type,
            key,
            id: key,
            isProfileShow,
          };
        })
      );
    } else {
      const key = uuidv4();
      const type = isParagraph ? ('paragraph' as ChatType) : defaultType;
      let isProfileShow = false;
      if (
        type != curLastType &&
        (type === ChatTypes.Sender || type === ChatTypes.Receiver)
      ) {
        isProfileShow = true;
      }
      curLastType = type;
      result.push({
        content,
        type,
        key: key,
        id: key,
        isProfileShow: isProfileShow,
      });
    }
  });

  return result;
};

export const parsePeekMessage = (
  chatItem: ChatItem[],
  character: IChatProfile,
  profile: IChatProfile | undefined,
  userName: string | undefined,
  backgroundImg?: IEFinderObject<'image'> | undefined | null
) => {
  let prevType = '';
  const initialChapterId = chatItem[0]?.chapter_id ?? null;
  const result = [...chatItem]
    .reverse()
    .map((item) => {
      if (item.role === 'human') {
        const parsedContent = parseChats(
          item.content,
          prevType,
          CHAT_TYPE.SENDER
        );
        const arr: TempPeekType[] = [];
        for (let i = 0; i < parsedContent?.length; i++) {
          const value = parsedContent[i];
          if (value.type === ChatTypes.Sender) {
            const message = getChatProperty(
              CHAT_TYPE.SENDER,
              value,
              profile
            ) as TempPeekType;
            message.chapter_id = item?.chapter_id;
            arr.push(message);
            prevType = ChatTypes.Sender;
          } else if (value.type === ChatTypes.Narration) {
            const message = getChatProperty(
              CHAT_TYPE.GENERAL,
              value
            ) as TempPeekType;
            message.chapter_id = item?.chapter_id;

            arr.push(message);
            prevType = ChatTypes.Narration;
          } else if (value.type === ChatTypes.Paragraph) {
            const message = getChatProperty(
              CHAT_TYPE.GENERAL,
              value,
              profile
            ) as TempPeekType;
            message.chapter_id = item?.chapter_id;

            arr.push(message);
            prevType = ChatTypes.Paragraph;
          }
        }

        return arr;
      } else {
        const parsedContent = parseChats(
          item.content,
          prevType,
          CHAT_TYPE.RECEIVER
        );

        const arr = [];

        for (let i = 0; i < parsedContent?.length; i++) {
          const value = parsedContent[i];
          if (value.type === ChatTypes.Receiver) {
            const message = getChatProperty(
              CHAT_TYPE.RECEIVER,
              value,
              character
            ) as TempPeekType;
            message.chapter_id = item?.chapter_id;

            arr.push(message);
            prevType = ChatTypes.Receiver;
          } else if (value.type === ChatTypes.Narration) {
            const message = getChatProperty(
              CHAT_TYPE.GENERAL,
              value
            ) as TempPeekType;
            message.chapter_id = item?.chapter_id;

            arr.push(message);
            prevType = ChatTypes.Narration;
          } else if (value.type === ChatTypes.Paragraph) {
            const message = getChatProperty(
              CHAT_TYPE.GENERAL,
              value
            ) as TempPeekType;
            message.chapter_id = item?.chapter_id;
            arr.push(message);
            prevType = ChatTypes.Paragraph;
          }
        }

        return arr;
      }
    })
    .flat();

  const initialNarration = {
    id: uuidv4(),
    object: {
      text: '인공지능에 의해 생성되는 것으로 대화의 진실성이나\n정확성이 보장되지 않을 수 있습니다',
    },
    objectType: ChatItemTypes.Narration,
    isProfileShow: false,
    chapter_id: initialChapterId,
    style: {
      bubbleColor: '#ffffff',
      textColor: '#000000',
      textAlign: 'initial',
      textStyle: 'initial',
      fontWeight: 'normal',
      wordBreak: 'normal',
    },
  };

  const startNarration = {
    id: uuidv4(),
    chapter_id: initialChapterId,
    object: {
      text: userName
        ? `${getJosa(
            character?.name,
            '와'
          )} ${userName}님이 플레이에서 나눈 대화를 작품처럼 감상해 보세요!`
        : `${getJosa(
            character?.name,
            '와'
          )} 채티즈가 플레이에서 나눈 대화를 작품처럼 감상해 보세요!`,
    },
    objectType: ChatItemTypes.Narration,
    isProfileShow: false,
    style: {
      bubbleColor: '#ffffff',
      textColor: '#000000',
      textAlign: 'initial',
      textStyle: 'initial',
      fontWeight: 'normal',
      wordBreak: 'normal',
    },
  };

  if (backgroundImg?.key) {
    const backgroundEfect = {
      id: uuidv4(),
      chapter_id: initialChapterId,
      is_show_profile: true,
      object: {
        color: null,
        name: 'BACKGROUND_IMAGE_CHANGE',
        options: {
          color: null,
          image: backgroundImg,
        },
      },
      objectType: ChatItemTypes.Effect,
    };
    return [backgroundEfect, initialNarration, startNarration, ...result];
  }
  return [initialNarration, startNarration, ...result];
};

export const getChatProperty = (
  type: (typeof CHAT_TYPE)[keyof typeof CHAT_TYPE],
  value: ChatbotChat,
  character?: IChatProfile | null | undefined
) => {
  const id = value.id;

  if (type === CHAT_TYPE.SENDER) {
    return {
      character: {
        displayName: character?.name ?? '나',
        id: character?.id.toString() ?? 'me',
        isPov: false,
        name: character?.name ?? '나',
        profileImage: character?.profile_image?.key
          ? (character?.profile_image as EFinderImageObject)
          : undefined,
        isOmniscient: false,
        isSender: true,
      },
      character_id: character?.id.toString(),
      id: value.id,
      object: { text: value.content },
      objectType: ChatItemTypes.Text,
      isProfileShow: !!value?.isProfileShow,
      style: {
        bubbleColor: '#ffffff',
        textColor: '#000000',
        textAlign: 'initial',
        textStyle: 'initial',
        fontWeight: 'normal',
        wordBreak: 'normal',
      },
    };
  } else if (type === CHAT_TYPE.GENERAL) {
    return getGeneralChat(id, value.content);
  } else if (type === CHAT_TYPE.RECEIVER) {
    return {
      character: {
        displayName: character?.name ?? '나',
        id: character?.id.toString() + '-receiver' ?? 'me',
        isPov: false,
        name: character?.name ?? '나',
        profileImage: character?.profile_image?.key
          ? (character?.profile_image as EFinderImageObject)
          : undefined,
        isOmniscient: false,
        isSender: false,
      },
      character_id: character?.id.toString() + '-receiver',
      id: id,
      object: { text: value.content },
      objectType: ChatItemTypes.Text,
      isProfileShow: !!value?.isProfileShow,
      style: {
        bubbleColor: '#ffffff',
        textColor: '#000000',
        textAlign: 'initial',
        textStyle: 'initial',
        fontWeight: 'normal',
        wordBreak: 'normal',
      },
    };
  } else if (type === CHAT_TYPE.NARRATION) {
    return {
      id: id,
      object: { text: value.content },
      objectType: ChatItemTypes.Narration,
      isProfileShow: false,
      style: {
        bubbleColor: '#ffffff',
        textColor: '#000000',
        textAlign: 'initial',
        textStyle: 'initial',
        fontWeight: 'normal',
        wordBreak: 'normal',
      },
    };
  }

  return {
    character: {
      displayName: '나',
      id: 'me',
      isPov: false,
      name: '나',
      profileImage: undefined,
      isOmniscient: false,
      isSender: true,
    },
    character_id: character?.id.toString(),
    id: id,
    object: { text: '' },
    objectType: ChatItemTypes.Text,
    isProfileShow: false,
    style: {
      bubbleColor: '#ffffff',
      textColor: '#000000',
      textAlign: 'initial',
      textStyle: 'initial',
      fontWeight: 'normal',
      wordBreak: 'normal',
    },
  };
};

export type TempPeekType =
  | {
      character: {
        displayName: string;
        id: string;
        isPov: boolean;
        name: string;
        isOmniscient: boolean;
        isSender: boolean;
      };
      character_id: string;
      id: string;
      object: {
        text: string;
      };
      objectType: ChatItemTypes;
      isProfileShow: boolean;
      style: { textColor?: string };
      chapter_id?: number | null;
    }
  | {
      id: string;
      object: {
        text: string;
      };
      character_id?: string;
      style: { textColor?: string };
      objectType: string;
      chapter_id?: number | null;
    }
  | {
      id: number;
      object: {
        text: string;
      };
      style: { textColor?: string };
      objectType: string;
      characterId: string;
      character_id?: string;
      chapter_id?: number | null;
    }
  | {
      id: string;
      is_show_profile: boolean;
      chapter_id?: number | null;
      character_id?: string;
      style: { textColor?: string };
      object: {
        color?: null;
        name?: string;
        options: {
          color?: null;
          image?: IEFinderObject<'image'> | undefined | null;
        };
      };
      objectType: string;
    };

export const getDraft = (
  leftCharacter?: IChatProfile | null | undefined,
  sender?: IChatProfile | null | undefined,
  messages?: TempPeekType[] | null | undefined,
  title?: string | null | undefined
) => {
  return {
    characters: [
      {
        name: 'General',
        id: '0',
        displayName: 'General',
      },
      {
        displayName: sender?.name ?? '나',
        id: sender?.id.toString() ?? 'me',
        isPov: false,
        name: sender?.name ?? '나',
        profileImage: sender?.profile_image?.key
          ? (sender?.profile_image as EFinderImageObject)
          : undefined,
      },
      {
        displayName: leftCharacter?.name,
        id: leftCharacter?.id.toString() + '-receiver',
        isPov: false,
        name: leftCharacter?.name,
        profileImage: leftCharacter?.profile_image?.key
          ? leftCharacter?.profile_image
          : undefined,
      },
    ],
    name: title ?? '',
    created: new Date().getTime(),
    currentSceneIndex: 0,
    version: '1.0',
    viewType: 'CHAT',
    scenes: [
      {
        id: uuidv4(),
        senderIds: [sender?.id.toString()] ?? ['me'],
        name: '',
        items: messages,
      },
    ],
  };
};

export const removeSiblingNodesAfterId = (id: string) => {
  const element = document.getElementById(id);
  if (!element || !element.parentNode) return;

  const siblingsToRemove: Node[] = [];
  let found = false;

  for (const sibling of element.parentNode.childNodes) {
    if (found) siblingsToRemove.push(sibling);
    if (sibling === element) found = true;
  }

  siblingsToRemove.forEach((sibling) =>
    sibling.parentNode?.removeChild(sibling)
  );
};

export const hideSiblingNodesAfterId = (id: string) => {
  const element = document.getElementById(id);
  if (!element || !element.parentNode) return;

  let found = false;

  for (const sibling of element.parentNode.childNodes) {
    if (found && sibling instanceof HTMLElement) {
      sibling.style.display = 'none';
    }
    if (sibling === element) found = true;
  }
};

export const getLastSenderTag = (id?: string | null) => {
  if (id) {
    return document.getElementById(id);
  }

  const element = document.querySelector('.cv2-viewer__content-wrap');
  const siblingSender = Array.from(element?.children || [])
    .reverse()
    .find((sibling: Element) =>
      sibling.classList.contains('cv2-chat--sender')
    ) as HTMLElement;

  return siblingSender;
};

const checkInnerText = (text: string) => {
  if (text.length >= 2) {
    return '**';
  }
  return text.slice(1, -1).trim() === '' ? '**' : text;
};

export const checkHasEmptyParagraph = (text: string) => {
  const regex = /\*((?!\n)[\s]+)?\*/g;
  const matches = text.match(regex);

  if (matches?.length === 0) {
    return false;
  }

  return (
    matches &&
    matches?.length > 0 &&
    matches.every((match) => checkInnerText(match) === '**')
  );
};

export const removeParagraph = (text: string) => {
  const regex = /\*((?!\n)[\s]+)?\*|\n\*((?!\n)[\s]+)?\*/g;

  return text.replace(regex, '');
};
export const getChatCustomProperty = (
  type: (typeof CHAT_TYPE)[keyof typeof CHAT_TYPE],
  value: {
    content: string;
    type?: string;
  },
  color?: string,
  id?: string
): Chat | Effect => {
  const textColor = color ?? '#5B5B5B';

  return {
    character: {
      displayName: 'General',
      id: '0',
      isPov: false,
      name: 'General',
      isOmniscient: true,
      isSender: false,
    },
    id: id ?? '0',
    object: { text: value.content },
    objectType: ChatItemTypes.Text,
    isProfileShow: false,
    style: {
      bubbleColor: '"rgba(255, 255, 255, 0)"',
      textColor: textColor,
      textAlign: 'center',
      textStyle: '"initial"',
      fontWeight: '"bold"',
      wordBreak: 'normal',
      fontFamily: '',
    },
  };
};

export const clearChat = () => {
  const parentElement = document.querySelector('.cv2-viewer__content-wrap');
  if (parentElement) {
    const elementsToRemove = Array.from(parentElement.children).filter(
      (child) => !child.classList.contains('cv2-push')
    );

    elementsToRemove.forEach((element) => {
      parentElement.removeChild(element);
    });
  }
};

export const scrollToVpElement = (
  container: HTMLElement | null | undefined,
  targetElement: HTMLElement | null,
  height: number
) => {
  if (!container || !targetElement) {
    return;
  }

  const screenCenter = window.innerHeight * 0.4;
  const targetTop = targetElement.getBoundingClientRect().top;

  if (targetTop) {
    container.scrollTop += targetTop - screenCenter - height;
  }
};

export const persistContainerScroll = (
  selector: string,
  prevScrollHeight: number
) => {
  const updatedContainer = document.querySelector(selector);
  const newHeight = updatedContainer?.scrollHeight ?? 0;

  if (updatedContainer) {
    updatedContainer.scrollTop = newHeight - prevScrollHeight;
  }
};

export const getLastViewableElement = () => {
  const viewport = document.querySelector('.cv2-viewer__content');
  if (!viewport) return;

  const viewportTop = viewport.scrollTop;
  const viewportBottom = viewportTop + viewport.clientHeight;

  const parentTag = document.querySelector('.cv2-viewer__content-wrap');
  if (!parentTag) return;

  const children = Array.from(parentTag.children) as HTMLElement[];

  let lastVisibleChild: HTMLElement | null = null;

  for (const child of children) {
    const rect = child.getBoundingClientRect();
    const elementTop = viewportTop + rect.top;
    const elementBottom = elementTop + rect.height;

    if (elementTop >= viewportTop && elementBottom <= viewportBottom - 100) {
      lastVisibleChild = child;
    }
  }

  return lastVisibleChild ?? null;
};

export const getChatRoomLastMessageContent = (item: string) => {
  if (!item) {
    return item;
  }
  const regex = /(\".*?\")|(\*.*?\*)|(?=\n\n)/;
  const arr = item.split(regex);
  const filteredArr = arr.filter((item) => {
    if (item == null) return false;
    if (item.trim().length === 0) return false;

    return true;
  });
  const content = filteredArr[filteredArr.length - 1]
    ?.replaceAll('"', '')
    ?.replaceAll('*', '');

  const reg = /(".*?")|(\*.*?\*)|(\?|\!|\.)|(?=\n\n)/;
  const parsedContent = content.split(reg).filter((item) => Boolean(item));

  const finalContent: string[] = [];

  for (let i = 0; i < parsedContent.length; i++) {
    if (
      ['?', '!', '.'].includes(parsedContent[i]) &&
      finalContent[finalContent.length - 1]
    ) {
      finalContent[finalContent.length - 1] += parsedContent[i];
    } else {
      finalContent.push(parsedContent[i]);
    }
  }

  const result = finalContent[finalContent?.length - 1];

  return result?.length > 22 ? result.slice(0, 20) + '...' : result;
};

export const getStoryChatRoomLastMessageContent = (item: string) => {
  if (!item) {
    return item;
  }

  const speakerRegex = /\n\n|\n/;
  const parts = item.split(speakerRegex);
  if (!parts?.length) {
    return getChatRoomLastMessageContent(item);
  }
  const newItem = parts[parts?.length - 1];
  let result = '';
  if (newItem?.startsWith('*')) {
    result = newItem?.replaceAll('"', '')?.replaceAll('*', '');
  } else {
    const name = newItem.split(':')[0];
    result = name
      ? newItem
          .replace(`${name}:`, '')
          ?.replaceAll('"', '')
          ?.replaceAll('*', '')
      : newItem;
  }

  return result?.length > 22 ? result.slice(0, 20) + '...' : result;
};

export const formatNumberWithUnit = (num: number) => {
  if (num >= 10000) {
    return `${Math.floor(num / 10000)}만명 pick 🔥`;
  } else if (num >= 1000) {
    return `${Math.floor(num / 1000)}천명 pick 🔥`;
  } else if (num >= 100) {
    return `${Math.floor(num / 100)}백명 pick 🔥`;
  } else {
    return;
  }
};

export const formatNumberMillion = (num: number) => {
  if (!num || num === 0) {
    return '0';
  }
  if (num < 10000) {
    return formatComma(num);
  } else if (num < 100000000) {
    const koreanNum = (num / 10000).toFixed(1);
    return koreanNum.replace('.0', '') + '만';
  } else {
    const koreanNum = (num / 100000000).toFixed(1);
    return koreanNum.replace('.0', '') + '억';
  }
};

export type progressStepType = { icon: string; color: string; text: string[] };

export const getProgressStepValue = (step: number): progressStepType => {
  switch (step) {
    case 1:
      return {
        icon: '#E5CCFF',
        color: '#F2E5FF',
        text: [
          '우리 아직 그런 사이 아니잖아..?',
          '나.. 아직 널 모르겠어',
          '천천히. 우리에겐 시간이 많으니까',
        ],
      };
    case 2:
      return {
        icon: '#CC99FF',
        color: '#C9F',
        text: [
          '이런게 설렘인가? 생각보다 기분 좋네',
          '너.. 조금 귀여워 보인다?',
          '오랜만에 느껴보는 설렘이야',
        ],
      };
    case 3:
      return {
        icon: '#93F',
        color: '#93F',
        text: [
          '너도 나와 같은 감정일까? 궁금해',
          '내가 너를 좋아해도 될까..?',
          '벌써? 좀 더 이따가 가면 안돼?',
        ],
      };
    case 4:
      return {
        icon: '#60C',
        color: '#D249E8',
        text: [
          '이런 내 모습 너한테만 보여주는거야',
          '내 마음이 느껴지니? 이 두근거림',
          '내가 좋아하는건 너야. 다른건 필요없어 ',
        ],
      };
    case 5:
      return {
        icon: '#FF07F5',
        color: 'linear-gradient(57deg, #FF07F5 19.7%, #ED0064 80.3%)',
        text: [
          '미치겠다. 네가 너무 좋아져버렸어ㅎ',
          '오늘 집에 안보낼거야. 그런 줄 알아.',
          '네가 있어야할 곳은 여기야. 내 옆자리',
        ],
      };
    default:
      return {
        icon: '💛',
        color: 'linear-gradient(57deg, #FF07F5 19.7%, #ED0064 80.3%)',
        text: [
          '우리 아직 그런 사이 아니잖아..?',
          '나.. 아직 널 모르겠어',
          '천천히. 우리에겐 시간이 많으니까',
        ],
      };
  }
};

export const getParsedMessage = (item: IChat) => {
  let prevType = '';

  const id = item?.id?.toString();
  const origin = item.content;
  const isStart = !!item.is_start_message;
  const type = item.role === 'ai' ? MessageTypes.Receiver : MessageTypes.Sender;
  const chats =
    item.role === 'ai'
      ? parseChats(origin, prevType, MessageTypes.Receiver)
      : parseChats(origin, prevType, ChatTypes.Sender);
  prevType = chats[chats?.length - 1]?.type;

  return {
    chats: chats,
    type: type,
    id: id ?? '',
    origin: origin,
    isEdited: false,
    isStart: isStart,
  };
};

export const removeEmptyFirstMessageReply = (
  values: ICharacterCreateForm | IChemingCharacterCreateForm
) => {
  const valuesObj = JSON.parse(JSON.stringify(values)) as
    | ICharacterCreateForm
    | IChemingCharacterCreateForm;

  if (valuesObj.situation_form.start_message_replies.length) {
    valuesObj.situation_form.start_message_replies =
      valuesObj.situation_form.start_message_replies.filter(
        (reply) => reply.content !== ''
      );
  }
  return valuesObj;
};

export const checkDiffEditCharacterForm = (
  prevData: ICharacterCreateForm | IChemingCharacterCreateForm,
  newData: CharacterEditForm
) => {
  const prevValue = {
    personality: prevData.character_form.information.personality,
    tone_of_voice: prevData.character_form.information.tone_of_voice,
    characteristics: prevData.character_form.information.characteristics,
    relationship: prevData.profile_form.information.relationship,
  } as CharacterEditForm;

  return Object.keys({ ...prevValue, ...newData }).some(
    (key) =>
      prevValue[key as keyof CharacterEditForm] !==
      newData[key as keyof CharacterEditForm]
  );
};
